import type { SADropdownOption } from '@missionlabs/smartagent-app-components/dist/Dropdown'
import type { AllThreadsAndMessages } from '@missionlabs/smartagent-service-thread'
import type { ThunkDispatch } from '@reduxjs/toolkit'
import { IEmailAttachment } from 'components/EmailMessage/types'
import { getActionAudit } from 'services/api/api.automated-rules'
import * as ContactAPI from 'services/api/api.contact'
import { getDownloadURL } from 'services/api/api.thread'
import ContactState from 'store/contact/contact.state'
import RootState from 'store/state'
import { IInteraction } from 'views/Metrics/HistoricMetrics/types'
import {
    getThreadMessageGroups,
    transformContacts,
    transformTaskContacts,
} from 'views/Metrics/HistoricMetrics/utils'

import * as ContactSearchActions from './contactSearch.reducer'
import { getChannelGroup } from './contactSearch.utils'

import { paramsToQueryString } from 'utils'

import type { IETR } from '@missionlabs/smartagent-lib-shared'
import AppState from 'store/app/app.state'

export const {
    loadingInteractions,
    resetFormParams,
    setAgent,
    setAppContactId,
    setBargedOnly,
    setLiveVideoAssist,
    setCallDurationEndTime,
    setCallDurationStartTime,
    setChatEmail,
    setChatOrderNumber,
    setContactID,
    setCustomerPhone,
    setEmail,
    setEmailSearchType,
    setEndDate,
    setEndTime,
    setExternalID,
    setFormParams,
    setHoldDurationEndTime,
    setHoldDurationStartTime,
    setInitiationMethod,
    setInteractions,
    setKeyword,
    setQueue,
    setSelectedInteraction,
    setSocialHandle,
    setStartDate,
    setStartTime,
    setSystemPhoneNumber,
    setUniqueSocialID,
    updateScreenRecording,
    setAccountId,
} = ContactSearchActions

export const setChannel = (channel: SADropdownOption<any>) => {
    return ContactSearchActions.setChannel({
        ...channel,
        group: getChannelGroup(channel.data),
    })
}

function isSearchForChannelOfType(channel: string, queryString: string) {
    const qs = new URLSearchParams(queryString)
    return qs.get('channel') === channel
}

function createModifiedQueryString(
    queryString: string,
    modifications: Record<string, string | null>,
) {
    const qs = new URLSearchParams(queryString)

    Object.keys(modifications).forEach((key) => {
        const value = modifications[key]
        if (value === null) {
            qs.delete(key)
        } else {
            qs.set(key, value)
        }
    })

    return qs.toString()
}

function companyHasChannelOfType(channelType: string, appConfig: AppState['appConfig']) {
    return !!(appConfig.channels ?? []).find(
        (channel) => channel.value.toLowerCase() === channelType.toLowerCase(),
    )
}

export const fetchInteractions = (queryString: string) => {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, ContactSearchActions.ContactSearchAction>,
        getState: () => RootState,
    ) => {
        try {
            const { app, auth } = getState()

            const parseQuerystring = (qs: string) => {
                return qs.split('&').reduce((acc, val) => {
                    return { ...acc, [val.split('=')[0]]: val.split('=')[1] }
                }, {})
            }

            const decodeObject = (obj: Record<string, string>) => {
                const newObject: Record<string, string> = {}
                Object.keys(obj).forEach((key) => {
                    newObject[key] = decodeURIComponent(obj[key])
                })
                return newObject
            }

            const { ID: companyID, instance: { ID: instanceID } = {} } = app
            const { token } = auth

            if (!instanceID) throw new Error('No instance ID found')
            if (!token) throw new Error('No token found')

            dispatch(loadingInteractions(true))

            const { data: contacts } = await ContactAPI.getContacts(
                companyID,
                instanceID,
                token,
                queryString,
            )

            if (
                isSearchForChannelOfType('EMAIL', queryString) &&
                companyHasChannelOfType('WEBFORM', app.appConfig)
            ) {
                const newQueryString = createModifiedQueryString(queryString, {
                    channel: 'WEBFORM',
                    emailSearchType: null,
                })

                const { data: webformContacts } = await ContactAPI.getContacts(
                    companyID,
                    instanceID,
                    token,
                    newQueryString,
                )

                contacts.push(...webformContacts)
            }

            const taskContacts: ContactState[] = []
            const otherContacts: ContactState[] = []

            // Split contacts into two arrays with one array containing task contacts and the other array containing the remaining contacts
            contacts.forEach((contact) =>
                contact.channel === 'TASK'
                    ? taskContacts.push(contact)
                    : otherContacts.push(contact),
            )

            const params: Record<string, string> = parseQuerystring(queryString)
            const { channel, queueID, externalID, agentID, phonenumberE164 } = params

            const isFilteringRelatedToCTREventsOnly =
                externalID || queueID || agentID || phonenumberE164

            const channelsForETRSearching = ['ALL', 'SMS+DM', 'CHAT', 'EMAIL', 'TASK']

            const eventTraceRecords: IETR[] = []

            if (channelsForETRSearching.includes(channel) && !isFilteringRelatedToCTREventsOnly) {
                let etrQueryString = queryString

                if (channel !== 'SMS+DM')
                    etrQueryString = paramsToQueryString({
                        ...decodeObject(params),
                        ...(params.taskEmail && { email: decodeURIComponent(params.taskEmail) }),
                    })

                eventTraceRecords.push(...(await ContactAPI.getEventTraceRecords(etrQueryString)))
            }

            const threadMessageGroups = await getThreadMessageGroups(
                taskContacts,
                eventTraceRecords,
            )

            // If this is a keyword search then provide the keyword so that we can filter the threads as well as the taskContacts
            const keywordDecoded = decodeURIComponent(
                params['keyword']?.replace(/\+/g, ' '),
            )?.toLowerCase()
            const filteredThreadMessageGroups = params['keyword']
                ? (threadMessageGroups.map((thread) => {
                      return {
                          ...thread,
                          messages: thread.messages.filter((message) => {
                              return (
                                  message.content?.toLowerCase().includes(keywordDecoded) ||
                                  thread.subject?.toLowerCase().includes(keywordDecoded)
                              )
                          }),
                          allMessages: thread.messages,
                      }
                  }) as AllThreadsAndMessages[])
                : [...threadMessageGroups]

            const taskInteractions = transformTaskContacts(
                taskContacts,
                filteredThreadMessageGroups,
                eventTraceRecords,
            )
            const otherInteractions = transformContacts(otherContacts)
            const allInteractions = taskInteractions.concat(otherInteractions)
            dispatch(setInteractions(allInteractions))
        } catch (error) {
            console.error('Error fetching interactions: ', error)

            dispatch(setInteractions([]))
        }
    }
}

export function fetchAutomatedRulesAudit(interactionID: string, actionAuditID: string | undefined) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, ContactSearchActions.ContactSearchAction>,
        getState: () => RootState,
    ) => {
        if (!actionAuditID) {
            dispatch(
                ContactSearchActions.setAutomatedRulesAudit({
                    interactionID,
                    automatedRulesAudit: [],
                }),
            )
            return
        }

        try {
            const { app, auth } = getState()

            const { ID: companyID, instance: { ID: instanceID } = {} } = app
            const { token } = auth

            if (!companyID) throw new Error('No company ID found')
            if (!instanceID) throw new Error('No instance ID found')
            if (!token) throw new Error('No token found')

            const response = await getActionAudit(companyID, instanceID, token, actionAuditID)

            dispatch(
                ContactSearchActions.setAutomatedRulesAudit({
                    interactionID,
                    automatedRulesAudit: response.data,
                }),
            )
        } catch (error) {
            console.error('Error fetching automated rules audit data: ', error)
            dispatch(
                ContactSearchActions.setAutomatedRulesAudit({
                    interactionID,
                    automatedRulesAudit: [],
                }),
            )
        }
    }
}

export function fetchQueuedTasksAudit(interactionID: string) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, ContactSearchActions.ContactSearchAction>,
        getState: () => RootState,
    ) => {
        try {
            const { app, auth } = getState()

            const { ID: companyID, instance: { ID: instanceID } = {} } = app
            const { token } = auth

            if (!companyID) throw new Error('No company ID found')
            if (!instanceID) throw new Error('No instance ID found')
            if (!token) throw new Error('No token found')

            const response = await ContactAPI.getContactAudit(
                companyID,
                instanceID,
                token,
                interactionID,
            )

            dispatch(
                ContactSearchActions.setQueuedTasksAudit({
                    interactionID,
                    queuedTasksAudit: response.data,
                }),
            )
        } catch (error) {
            console.error('Error fetching queued tasks audit data: ', error)
            dispatch(
                ContactSearchActions.setQueuedTasksAudit({
                    interactionID,
                    queuedTasksAudit: [],
                }),
            )
        }
    }
}

export function fetchAttachmentUrls(
    attachments: IEmailAttachment[],
    messageId: string,
    threadId: string,
) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, ContactSearchActions.ContactSearchAction>,
        getState: () => RootState,
    ) => {
        try {
            const { app, auth } = getState()

            const { ID: instanceID } = app.instance ?? {}
            const { token } = auth

            if (!instanceID) throw new Error('No instance ID found')
            if (!token) throw new Error('No token found')

            const newAttachments: IEmailAttachment[] = await Promise.all(
                attachments.map(async (attachment) => {
                    const { s3Key, filename } = attachment
                    let url = ''
                    try {
                        url = (await getDownloadURL(threadId, messageId, encodeURI(s3Key))).url
                    } catch (error) {
                        console.error(`Error retrieving URL for ${s3Key}/${filename}: ${error}`)
                    }

                    return { ...attachment, s3URL: url }
                }),
            )
            dispatch(
                ContactSearchActions.addTransformedAttachments({
                    interactionID: messageId,
                    transformedAttachments: newAttachments,
                }),
            )
        } catch (error) {
            console.error('Error fetching attachment S3 urls:', error)

            dispatch(
                ContactSearchActions.addTransformedAttachments({
                    interactionID: messageId,
                    transformedAttachments: attachments,
                }),
            )
        }
    }
}

export const fetchInteractionAttributes = (interaction: IInteraction) => {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, ContactSearchActions.ContactSearchAction>,
    ) => {
        const { ID: interactionID, attributes: originalAttributes } = interaction

        try {
            const { attributes } = await ContactAPI.getContact(interactionID)

            dispatch(
                ContactSearchActions.setInteractionAttributes({
                    interactionID,
                    attributes,
                }),
            )
        } catch (error) {
            console.error('Error fetching interaction attributes:', error)

            dispatch(
                ContactSearchActions.setInteractionAttributes({
                    interactionID,
                    attributes: originalAttributes,
                }),
            )
        }
    }
}

export function accessScreenRecording(initialContactId: string, recordingDate: number) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, ContactSearchActions.ContactSearchAction>,
        getState: () => RootState,
    ) => {
        const state = getState()
        if (!state.app || !state.auth.token) {
            throw Error(`No app state set or not authorized`)
        }
        if (!initialContactId || !recordingDate) {
            throw Error(`Invalid params passed to screen recording`)
        }

        try {
            const response = await ContactAPI.getScreenRecording(
                initialContactId,
                state.app.ID,
                state.app.instance!.ID,
                state.auth.token!,
                recordingDate,
            )

            if (response.status === 200) {
                dispatch(ContactSearchActions.updateScreenRecording(response.data))
            }
        } catch (error) {
            dispatch(ContactSearchActions.updateScreenRecording({ signedUrl: '' }))
            console.log(error)
        }
    }
}

export function deleteScreenRecording(contactID: string, date: number) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, ContactSearchActions.ContactSearchAction>,
        getState: () => RootState,
    ): Promise<{ success: boolean; error?: string }> => {
        const state = getState() as RootState

        // Validate the state
        if (!state.app || !state.auth.token) {
            return { success: false, error: 'No app state set or not authorised' }
        }

        // Validate input parameters
        if (!contactID || !date) {
            return { success: false, error: 'Invalid params passed to screen recording' }
        }

        try {
            const response = await ContactAPI.deleteScreenRecording(
                state.app.ID,
                contactID,
                date,
                state.app.instance!.ID,
                state.auth.token!,
            )

            if (response.status === 200) {
                dispatch(ContactSearchActions.updateScreenRecording({ signedUrl: '' }))
                return { success: true }
            } else {
                return { success: false, error: `Unexpected response status: ${response.status}` }
            }
        } catch (error: any) {
            console.error('Error deleting screen recording:', error)
            return { success: false, error: error?.message || 'Failed to delete screen recording' }
        }
    }
}
